'use client';

import React, { useEffect, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import useFetchSnapshotPipePricing from '../../hooks/pricingSnapshot/useFetchSnapshotPipePricing';

const CostAssumptionsSummary = () => {
  const { formData, budgetSummaryRows, setAssumptionsSummaryRows } = useEstimator();
  const estimateId = formData.estimateId;
  const { pipePricing } = useFetchSnapshotPipePricing(estimateId);

  const rowData = useMemo(() => {
    if (!formData?.quantities || !budgetSummaryRows?.length || !pipePricing?.length) return [];

    const getNumber = (val) => parseFloat(val ?? '') || 0;

    const linePipeBorePipe = getNumber(budgetSummaryRows.find((item) => item.no === 6)?.rawPrice);
    const constructionLabor = getNumber(budgetSummaryRows.find((item) => item.no === 13)?.rawPrice);
    const landOwnerAndROW =
      getNumber(budgetSummaryRows.find((item) => item.no === 1)?.rawPrice) +
      getNumber(budgetSummaryRows.find((item) => item.no === 2)?.rawPrice);
    const freight = getNumber(budgetSummaryRows.find((item) => item.no === 11)?.rawPrice);

    const pipeLength = getNumber(formData?.quantities?.find((item) => item.rowNumber === 7)?.quantity);
    const diameter = getNumber(formData?.quantities?.find((item) => item.rowNumber === 1)?.quantity);

    const pipeRolledCoated = pipeLength ? linePipeBorePipe / (pipeLength * 5280) : 0;
    const constructionBaseLay = pipeLength ? constructionLabor / (pipeLength * 5280) : 0;
    const ftRow = pipeLength ? landOwnerAndROW / (pipeLength * 5280) : 0;

    const matchedPipe = pipePricing.find((pipe) => parseFloat(pipe.pipeOD) === diameter);
    const pipeBarePrice = matchedPipe?.pricePerTon
      ? `$${Number(matchedPipe.pricePerTon).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : '-';

    const hasCoatingPrices = formData?.pipeSpecs?.some(
      (row) => row?.coatingPrice && !isNaN(parseFloat(row.coatingPrice))
    );

    const pipeCoatingPriceTotal = hasCoatingPrices
      ? `$${formData.pipeSpecs
          .reduce((sum, row) => {
            const price = parseFloat(row?.coatingPrice ?? 0);
            return sum + (isNaN(price) ? 0 : price);
          }, 0)
          .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '-';

    const hasTons = formData?.pipeSpecs?.some((row) => row?.tons && !isNaN(parseFloat(row.tons)));
    const totalTonsValue = formData.pipeSpecs.reduce((sum, row) => {
      const tons = parseFloat(row?.tons ?? 0);
      return sum + (isNaN(tons) ? 0 : tons);
    }, 0);

    const stPipeCoated =
      hasTons && totalTonsValue > 0
        ? `$${(linePipeBorePipe / totalTonsValue).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : '-';

    const stPipeDelivered =
      hasTons && totalTonsValue > 0
        ? `$${((linePipeBorePipe + freight) / totalTonsValue).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : '-';

    return [
      { no: 1, assumption: '$/ft Pipe Rolled & Coated', value: `$${pipeRolledCoated.toFixed(2)}` },
      { no: 2, assumption: '$/ft construction base lay', value: `$${constructionBaseLay.toFixed(2)}` },
      { no: 3, assumption: '$/ft row', value: `$${ftRow.toFixed(2)}` },
      { no: 4, assumption: 'Pipe Coating Price', value: pipeCoatingPriceTotal },
      { no: 5, assumption: '$/ST Pipe Bare', value: pipeBarePrice },
      { no: 6, assumption: '$/ST Pipe Coated', value: stPipeCoated },
      { no: 7, assumption: '$/ST Pipe Delivered', value: stPipeDelivered },
    ];
  }, [formData, budgetSummaryRows, pipePricing]);

  useEffect(() => {
    setAssumptionsSummaryRows(rowData);
  }, [rowData, setAssumptionsSummaryRows]);

  const costAssumptionsColumns = [
    { headerName: 'Cost Assumption', field: 'assumption', flex: 2, cellStyle: { fontWeight: 500, fontSize: '14px' } },
    { headerName: 'Value', field: 'value', flex: 1, cellStyle: { fontSize: '14px' } },
  ];

  return (
    <Paper
      elevation={4}
      sx={{ borderRadius: 3, overflow: 'hidden', p: 0, backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0' }}
    >
      <Box sx={{ backgroundColor: '#223A64', py: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
          Cost Assumptions
        </Typography>
      </Box>
      <Box
        className="ag-theme-alpine"
        sx={{
          width: '100%',
          '& .ag-root-wrapper': { border: 'none' },
          '& .ag-header': { display: 'none' },
          '& .ag-row': { borderBottom: '1px solid #eee' },
          '& .ag-cell': { display: 'flex', alignItems: 'center' },
        }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={costAssumptionsColumns}
          headerHeight={0}
          domLayout="autoHeight"
          suppressMenuHide
          suppressCellFocus
          suppressMovableColumns
          suppressColumnVirtualisation
          defaultColDef={{ resizable: true, sortable: false, filter: false }}
        />
      </Box>
    </Paper>
  );
};

export default CostAssumptionsSummary;
